import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Instagram, Linkedin, Facebook } from 'lucide-react';
import { contactData } from '../data/mock';

const Contact = () => {
  const whatsappNumber = '917073620688';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [focusedField, setFocusedField] = useState(null);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const structuredMessage = [
      'Hello, I would like to inquire about your design services.',
      '',
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone || 'Not provided'}`,
      `Message: ${formData.message}`
    ].join('\n');

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(structuredMessage)}`;

    setSubmitMessage('Redirecting you to WhatsApp...');
    setIsSubmitting(false);
    setFormData({ name: '', email: '', phone: '', message: '' });
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => setSubmitMessage(''), 2500);
  };

  const iconMap = {
    instagram: Instagram,
    linkedin: Linkedin,
    facebook: Facebook
  };

  const socialColorMap = {
    instagram: 'hover:bg-gradient-to-r hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF]',
    linkedin: 'hover:bg-[#0A66C2]',
    facebook: 'hover:bg-[#1877F2]'
  };

  return (
    <section id="contact" className="py-24 bg-[#e8d5c4]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2
            className="text-5xl md:text-6xl font-bold text-black mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {contactData.title}
          </h2>
          <p
            className="text-lg md:text-xl text-black/70 max-w-3xl mx-auto"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {contactData.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="space-y-8">
              {/* Email */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-black">
                  <Mail size={24} className="text-[#e8d5c4]" />
                </div>
                <div>
                  <h3
                    className="text-xl font-bold text-black mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Email
                  </h3>
                  <a
                    href={`mailto:${contactData.email}`}
                    className="text-black/70 hover:text-black transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {contactData.email}
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-black">
                  <Phone size={24} className="text-[#e8d5c4]" />
                </div>
                <div>
                  <h3
                    className="text-xl font-bold text-black mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    WhatsApp
                  </h3>
                  <a
                    href={`https://wa.me/${whatsappNumber}`}
                    className="text-black/70 hover:text-black transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {contactData.phone}
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-black">
                  <MapPin size={24} className="text-[#e8d5c4]" />
                </div>
                <div>
                  <h3
                    className="text-xl font-bold text-black mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Location
                  </h3>
                  <p
                    className="text-black/70"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {contactData.address}
                  </p>
                </div>
              </div>

              {/* Social Links */}
              <div className="pt-8">
                <h3
                  className="text-xl font-bold text-black mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Follow Us
                </h3>
                <div className="flex space-x-4">
                  {contactData.socials.map((social) => {
                    const Icon = iconMap[social.icon] || Instagram;
                    const colorClass = socialColorMap[social.icon];
                    return (
                      <motion.a
                        key={social.name}
                        href={social.url}
                        whileHover={{ scale: 1.1, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        className={`p-3 bg-black transition-all duration-300 group ${colorClass}`}
                        aria-label={social.name}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon size={24} className="text-white" />
                      </motion.a>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-white p-10 md:p-12 shadow-2xl relative overflow-hidden border border-black/10"
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-black/10"></div>
            <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-black/10"></div>
            
            <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
              <div className="relative">
                <label
                  htmlFor="name"
                  className="block text-black font-bold mb-3 text-xs uppercase tracking-widest"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Full Name *
                </label>
                <motion.input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  required
                  whileFocus={{ scale: 1.01 }}
                  className="w-full px-6 py-4 bg-[#fafafa] border-b-3 border-black/30 focus:border-black focus:bg-white outline-none transition-all text-base"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.125rem' }}
                  placeholder="John Doe"
                />
                {focusedField === 'name' && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-black"
                  />
                )}
              </div>

              <div className="relative">
                <label
                  htmlFor="email"
                  className="block text-black font-bold mb-3 text-xs uppercase tracking-widest"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Email Address *
                </label>
                <motion.input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                  whileFocus={{ scale: 1.01 }}
                  className="w-full px-6 py-4 bg-[#fafafa] border-b-3 border-black/30 focus:border-black focus:bg-white outline-none transition-all text-base"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.125rem' }}
                  placeholder="john@example.com"
                />
                {focusedField === 'email' && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-black"
                  />
                )}
              </div>

              <div className="relative">
                <label
                  htmlFor="phone"
                  className="block text-black font-bold mb-3 text-xs uppercase tracking-widest"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Phone Number
                </label>
                <motion.input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                  whileFocus={{ scale: 1.01 }}
                  className="w-full px-6 py-4 bg-[#fafafa] border-b-3 border-black/30 focus:border-black focus:bg-white outline-none transition-all text-base"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.125rem' }}
                  placeholder="+1 (555) 000-0000"
                />
                {focusedField === 'phone' && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-black"
                  />
                )}
              </div>

              <div className="relative">
                <label
                  htmlFor="message"
                  className="block text-black font-bold mb-3 text-xs uppercase tracking-widest"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Your Message *
                </label>
                <motion.textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  required
                  rows="5"
                  whileFocus={{ scale: 1.01 }}
                  className="w-full px-6 py-4 bg-[#fafafa] border-b-3 border-black/30 focus:border-black focus:bg-white outline-none transition-all resize-none text-base"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.125rem', lineHeight: '1.8' }}
                  placeholder="Tell us about your dream project..."
                ></motion.textarea>
                {focusedField === 'message' && (
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-black"
                  />
                )}
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="w-full px-8 py-5 bg-black text-white font-bold uppercase tracking-widest flex items-center justify-center space-x-3 hover:bg-[#2a2a2a] transition-all disabled:opacity-50 relative overflow-hidden group shadow-lg hover:shadow-2xl"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}
              >
                <span className="relative z-10">{isSubmitting ? 'Sending Your Message...' : 'Send Message'}</span>
                {!isSubmitting && (
                  <Send size={20} className="relative z-10 group-hover:translate-x-2 transition-transform duration-300" />
                )}
                
                {/* Animated shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  animate={{
                    x: ['-100%', '200%']
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 3,
                    ease: 'linear'
                  }}
                />
              </motion.button>

              {submitMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center p-6 bg-black/5 border-l-4 border-black relative"
                >
                  <p
                    className="text-black font-bold text-lg"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    ✓ {submitMessage}
                  </p>
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
